import asyncCallback from '@/framework/logic/utils/asyncCallback';
import { isValidURL } from '@/model/routing/utils';
import Location from '@/ports-frontend/Location';
import MessageBus from '@/ports-frontend/MessageBus';

type UrlExtractor = (payload: string) => string | undefined;

const isSameDomain = (url: string, currentHostname: string): boolean => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === currentHostname;
  } catch (error) {
    console.error('## DeepLink: Failed to check domain for URL:', url, error);
    return false;
  }
};

const processExtractedUrl = (
  url: string | undefined,
  currentHostname: string
): void => {
  if (!url) {
    console.error('## DeepLink: No valid URL extracted from event');
    return;
  }

  if (!isValidURL(url)) {
    console.error('## DeepLink: Invalid URL:', url);
    return;
  }

  if (isSameDomain(url, currentHostname)) {
    const urlObj = new URL(url);
    Location.set(urlObj.pathname + urlObj.search + urlObj.hash);
  }
};

 /**
 * Extracts the deep link URL from a smartlink URL by decoding the 'deep_link_value' parameter.
 * 
 * @param smartlinkUrl - The smartlink URL containing the encoded deep link value
 * @returns The decoded deep link URL if extraction is successful, undefined otherwise
 * 
 * @example
 * ```typescript
 * const smartlink = 'https://wh-casino-es.onelink.me/ru8I?af_xp=custom&pid=GamingQA&deep_link_value=https%3A%2F%2Fvegas.williamhill.com%2Fpromotions%2F10fs-algn-010825';
 * const extracted = extractSmartlinkUrl(smartlink);
 * // Returns: 'https://vegas.williamhill.com/promotions/10fs-algn-010825'
 * ```
 */
const extractSmartlinkUrl: UrlExtractor = (smartlinkUrl: string): string | undefined => {
  try {
    if (smartlinkUrl.includes('deep_link_value=')) {
      const urlEncoded = smartlinkUrl.split('deep_link_value=')[1];
      const decodedUrl = decodeURIComponent(urlEncoded);
      return decodedUrl;
    } else {
      console.error('## DeepLink: No deep_link_value parameter found in smartlink:', smartlinkUrl);
    }
    
    return undefined;
  } catch (error) {
    console.error('## DeepLink: Failed to extract smartlink URL:', smartlinkUrl, error);
    return undefined;
  }
};

/**
 * Extracts a URL from a push notification payload.
 * 
 * This function parses a JSON string containing push notification data and extracts
 * the target URL from the deep link structure. It looks for the '^d' field which
 * contains a deep link URL, then extracts the 'url' parameter from that deep link.
 * 
 * @param pushPayload - The JSON string containing the push notification payload
 * @returns The extracted URL string, or undefined if parsing fails or URL is not found
 * 
 * @example
 * ```typescript
 * const payload = JSON.stringify({
 *   "com.urbanairship.metadata": "eyJ2ZXJzaW9uX2lkIjoxLCJ0aW1lIjoxNzU0MjiOmZhbHNlLCJjb250ZXh0Ijp7fX0=",
 *   "_": "004363b7-54b1-4b58-b204-b5421c98ac68",
 *   "^d": "whgaming://?action=openURL&url=https://www.ynet.co.il/home/0,7340,L-8,00.html",
 *   "aps": {
 *     "alert": "Push test all",
 *     "badge": 1,
 *     "sound": "1"
 *   }
 * });
 * 
 * const extractedUrl = extractPushUrl(payload);
 * // Returns: "https://www.ynet.co.il/home/0,7340,L-8,00.html"
 * ```
 */
const extractPushUrl: UrlExtractor = (pushPayload: string): string | undefined => {
  try {
    const parsedData = JSON.parse(pushPayload);
    const actionUrl = parsedData['^d'];

    if (!actionUrl) {
      console.error('## DeepLink: Missing ^d field in parsed data:', parsedData);
      return undefined;
    }

    const deepLinkUrl = new URL(actionUrl);
    const url = deepLinkUrl.searchParams.get('url');

    if (!url) {
      console.error('## DeepLink: Invalid or missing URL parameter in action URL:', actionUrl);
      return undefined;
    }

    return url;
  } catch (error) {
    console.error('## DeepLink: Failed to parse push payload:', pushPayload, error);
    return undefined;
  }
};

const eventHandlers = {
  'wh.hybrid.event.deeplink.smartlink': extractSmartlinkUrl,
  'wh.hybrid.event.deeplink.push': extractPushUrl,
} as const;

export const createDeepLinkListener = () => {
  return asyncCallback(async () => {
    try {
      const currentLocation = await Location.get();
      const currentHostname = currentLocation.hostname;

      const unsubscribe = await MessageBus.onSubscribe(
        async ({
          message,
          payload,
        }: {
          message: string;
          payload: { link: string };
        }) => {
          try {
            const extractor = eventHandlers[message as keyof typeof eventHandlers];
            
            if (!extractor) {
              console.warn('## DeepLink: No handler found for event type:', message);
              return;
            }

            const extractedUrl = extractor(payload.link);
            
            processExtractedUrl(extractedUrl, currentHostname);

          } catch (error) {
            console.error('## DeepLink: Error processing deep link:', error, {
              message,
              payload,
            });
          }
        },
        Object.keys(eventHandlers)
      );

      return unsubscribe;
    } catch (error) {
      console.error('## DeepLink: Error starting deep link listener:', error);
      throw error;
    }
  });
};
